import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';
import './TastingNoteForm.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const STEPS = ['wine', 'appearance', 'nose', 'palate', 'assessment', 'notes'];

const CLARITY_OPTIONS = ['Clear', 'Hazy'];
const INTENSITY_OPTIONS = ['Light', 'Medium-', 'Medium', 'Medium+', 'Deep'];
const COLOR_RED = ['Purple', 'Ruby', 'Garnet', 'Tawny'];
const COLOR_WHITE = ['Lemon-green', 'Lemon', 'Gold', 'Amber'];
const COLOR_ROSE = ['Pink', 'Salmon', 'Orange'];
const CONDITION_OPTIONS = ['Clean', 'Unclean', 'Faulty'];
const AROMA_TAGS = [
  'Citrus', 'Stone Fruit', 'Tropical', 'Red Fruit', 'Black Fruit', 'Dried Fruit',
  'Floral', 'Herbal', 'Green', 'Spice', 'Oak', 'Earthy', 'Mineral', 'Yeast',
  'Butter', 'Vanilla', 'Toast', 'Smoke', 'Leather', 'Mushroom',
];
const SWEETNESS_OPTIONS = ['Dry', 'Off-dry', 'Medium-dry', 'Medium-sweet', 'Sweet'];
const ACIDITY_OPTIONS = ['Low', 'Medium-', 'Medium', 'Medium+', 'High'];
const TANNIN_OPTIONS = ['Low', 'Medium-', 'Medium', 'Medium+', 'High'];
const BODY_OPTIONS = ['Light', 'Medium-', 'Medium', 'Medium+', 'Full'];
const FINISH_OPTIONS = ['Short', 'Medium-', 'Medium', 'Medium+', 'Long'];
const QUALITY_OPTIONS = ['Faulty', 'Poor', 'Acceptable', 'Good', 'Very Good', 'Outstanding'];
const READINESS_OPTIONS = ['Too young', 'Can drink now, but has potential', 'Drink now', 'Past its best'];

function TastingNoteForm({ instance, onSave, onCancel, showMessage }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Wine selection
  const [wineSearch, setWineSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedWineId, setSelectedWineId] = useState(null);
  const [selectedWineName, setSelectedWineName] = useState('');

  // Appearance
  const [clarity, setClarity] = useState('Clear');
  const [colorIntensity, setColorIntensity] = useState('Medium');
  const [color, setColor] = useState('');

  // Nose
  const [noseCondition, setNoseCondition] = useState('Clean');
  const [noseIntensity, setNoseIntensity] = useState('Medium');
  const [aromas, setAromas] = useState([]);

  // Palate
  const [sweetness, setSweetness] = useState('Dry');
  const [acidity, setAcidity] = useState('Medium');
  const [tannin, setTannin] = useState('Medium');
  const [body, setBody] = useState('Medium');
  const [finish, setFinish] = useState('Medium');

  // Assessment
  const [quality, setQuality] = useState('Good');
  const [readiness, setReadiness] = useState('Drink now');
  const [rating, setRating] = useState(3);

  // Notes
  const [freeNotes, setFreeNotes] = useState('');
  const [foodPairings, setFoodPairings] = useState('');

  const handleSearchWine = useCallback(async () => {
    if (!wineSearch.trim()) return;
    try {
      const data = await apiFetchJson(instance, `/api/wine/search?query=${encodeURIComponent(wineSearch)}`);
      setSearchResults(unwrap(data));
    } catch (e) {
      console.error('Wine search failed:', e);
    }
  }, [instance, wineSearch]);

  const selectWine = useCallback((wine) => {
    setSelectedWineId(wine.id);
    setSelectedWineName(wine.name);
  }, []);

  const toggleAroma = useCallback((aroma) => {
    setAromas(prev =>
      prev.includes(aroma)
        ? prev.filter(a => a !== aroma)
        : [...prev, aroma]
    );
  }, []);

  const handleSubmit = async () => {
    if (!selectedWineId) {
      showMessage(t('grapeEscape.tastingNote.selectWine', 'Please select a wine first.'), 'error');
      return;
    }
    setSaving(true);
    try {
      await apiFetchJson(instance, '/api/tasting/notes', {
        method: 'POST',
        body: JSON.stringify({
          wineId: selectedWineId,
          tastingDate: new Date().toISOString(),
          clarity,
          colorIntensity,
          color,
          noseCondition,
          noseIntensity,
          aromas,
          sweetness,
          acidity,
          tannin,
          body,
          finish,
          qualityLevel: quality,
          readiness,
          rating,
          notes: freeNotes,
          foodPairings,
        }),
      });
      onSave();
    } catch (e) {
      console.error('Failed to save tasting note:', e);
      showMessage(t('grapeEscape.tastingNote.saveError', 'Failed to save tasting note.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const currentStep = STEPS[step];
  const isLastStep = step === STEPS.length - 1;

  return (
    <div className="ge-tasting-form">
      <div className="ge-section-header">
        <h3>{t('grapeEscape.tastingNote.title', 'WSET Tasting Note')}</h3>
        <button className="ge-btn-secondary" onClick={onCancel}>
          {t('grapeEscape.tastingNote.cancel', 'Cancel')}
        </button>
      </div>

      {/* Step Indicator */}
      <div className="ge-steps">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <div className="ge-step-line" />}
            <div
              className={`ge-step-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
              onClick={() => setStep(i)}
            >
              {i + 1}
            </div>
          </React.Fragment>
        ))}
      </div>

      <div className="ge-step-content ge-card">
        {/* Step 1: Wine Selection */}
        {currentStep === 'wine' && (
          <div>
            <h4>{t('grapeEscape.tastingNote.selectWine', 'Select Wine')}</h4>
            <div className="ge-search-row">
              <input
                type="text"
                value={wineSearch}
                onChange={e => setWineSearch(e.target.value)}
                placeholder={t('grapeEscape.tastingNote.searchPlaceholder', 'Search wines or cellar...')}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleSearchWine())}
              />
              <button type="button" className="ge-btn-secondary" onClick={handleSearchWine}>
                {t('grapeEscape.tastingNote.search', 'Search')}
              </button>
            </div>
            {selectedWineName && (
              <div className="ge-selected-wine">
                ✓ {selectedWineName}
              </div>
            )}
            {searchResults.length > 0 && (
              <div className="ge-search-results">
                {searchResults.map(wine => (
                  <div
                    key={wine.id}
                    className={`ge-search-result ${selectedWineId === wine.id ? 'selected' : ''}`}
                    onClick={() => selectWine(wine)}
                  >
                    <span className="ge-search-name">{wine.name}</span>
                    <span className="ge-search-meta">
                      {wine.producer} {wine.vintage && `· ${wine.vintage}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Appearance */}
        {currentStep === 'appearance' && (
          <div>
            <h4>{t('grapeEscape.tastingNote.appearance', 'Appearance')}</h4>
            <div className="ge-form-grid">
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.clarity', 'Clarity')}</label>
                <select value={clarity} onChange={e => setClarity(e.target.value)}>
                  {CLARITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.colorIntensity', 'Intensity')}</label>
                <select value={colorIntensity} onChange={e => setColorIntensity(e.target.value)}>
                  {INTENSITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.color', 'Color')}</label>
                <select value={color} onChange={e => setColor(e.target.value)}>
                  <option value="">—</option>
                  <optgroup label="Red">
                    {COLOR_RED.map(c => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                  <optgroup label="White">
                    {COLOR_WHITE.map(c => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                  <optgroup label="Rosé">
                    {COLOR_ROSE.map(c => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Nose */}
        {currentStep === 'nose' && (
          <div>
            <h4>{t('grapeEscape.tastingNote.nose', 'Nose')}</h4>
            <div className="ge-form-grid">
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.condition', 'Condition')}</label>
                <select value={noseCondition} onChange={e => setNoseCondition(e.target.value)}>
                  {CONDITION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.noseIntensity', 'Intensity')}</label>
                <select value={noseIntensity} onChange={e => setNoseIntensity(e.target.value)}>
                  {INTENSITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="ge-aroma-section">
              <label>{t('grapeEscape.tastingNote.aromas', 'Aromas')}</label>
              <div className="ge-aroma-chips">
                {AROMA_TAGS.map(aroma => (
                  <button
                    key={aroma}
                    type="button"
                    className={`ge-aroma-chip ${aromas.includes(aroma) ? 'active' : ''}`}
                    onClick={() => toggleAroma(aroma)}
                  >
                    {aroma}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Palate */}
        {currentStep === 'palate' && (
          <div>
            <h4>{t('grapeEscape.tastingNote.palate', 'Palate')}</h4>
            <div className="ge-form-grid">
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.sweetness', 'Sweetness')}</label>
                <select value={sweetness} onChange={e => setSweetness(e.target.value)}>
                  {SWEETNESS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.acidity', 'Acidity')}</label>
                <select value={acidity} onChange={e => setAcidity(e.target.value)}>
                  {ACIDITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.tannin', 'Tannin')}</label>
                <select value={tannin} onChange={e => setTannin(e.target.value)}>
                  {TANNIN_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.body', 'Body')}</label>
                <select value={body} onChange={e => setBody(e.target.value)}>
                  {BODY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.finish', 'Finish')}</label>
                <select value={finish} onChange={e => setFinish(e.target.value)}>
                  {FINISH_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Assessment */}
        {currentStep === 'assessment' && (
          <div>
            <h4>{t('grapeEscape.tastingNote.assessment', 'Assessment')}</h4>
            <div className="ge-form-grid">
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.quality', 'Quality Level')}</label>
                <select value={quality} onChange={e => setQuality(e.target.value)}>
                  {QUALITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.readiness', 'Readiness')}</label>
                <select value={readiness} onChange={e => setReadiness(e.target.value)}>
                  {READINESS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.tastingNote.rating', 'Rating (1–5)')}</label>
                <div className="ge-rating-input">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      className={`ge-star ${rating >= star ? 'active' : ''}`}
                      onClick={() => setRating(star)}
                    >
                      ⭐
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Notes */}
        {currentStep === 'notes' && (
          <div>
            <h4>{t('grapeEscape.tastingNote.notesStep', 'Additional Notes')}</h4>
            <div className="ge-form-grid">
              <div className="ge-form-group full-width">
                <label>{t('grapeEscape.tastingNote.freeNotes', 'Tasting Notes')}</label>
                <textarea
                  rows="4"
                  value={freeNotes}
                  onChange={e => setFreeNotes(e.target.value)}
                  placeholder={t('grapeEscape.tastingNote.notesPlaceholder', 'Your impressions...')}
                />
              </div>
              <div className="ge-form-group full-width">
                <label>{t('grapeEscape.tastingNote.foodPairings', 'Food Pairings')}</label>
                <input
                  type="text"
                  value={foodPairings}
                  onChange={e => setFoodPairings(e.target.value)}
                  placeholder={t('grapeEscape.tastingNote.pairingsPlaceholder', 'e.g. Grilled lamb, aged cheese')}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="ge-step-nav">
        <button
          className="ge-btn-secondary"
          onClick={() => setStep(s => s - 1)}
          disabled={step === 0}
        >
          ← {t('grapeEscape.tastingNote.back', 'Back')}
        </button>
        {isLastStep ? (
          <button className="ge-btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving
              ? t('grapeEscape.tastingNote.saving', 'Saving...')
              : t('grapeEscape.tastingNote.submit', 'Save Tasting Note')}
          </button>
        ) : (
          <button className="ge-btn-primary" onClick={() => setStep(s => s + 1)}>
            {t('grapeEscape.tastingNote.next', 'Next')} →
          </button>
        )}
      </div>
    </div>
  );
}

export default TastingNoteForm;
